import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, Tabs, Tab, ProgressBar, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import './CommandesCours.css';

export default function CommandesCours() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('clients');
  const [selectedCommande, setSelectedCommande] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailType, setDetailType] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Pagination functions
  const paginateData = (dataArray, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return dataArray?.slice(startIndex, endIndex) || [];
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getTotalPages = (dataLength) => {
    return Math.ceil((dataLength || 0) / itemsPerPage);
  };

  // Pagination Component
  const PaginationComponent = ({ currentPage, totalPages, onPageChange }) => {
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };

    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
        <div className="text-muted small">
          Page {currentPage} sur {totalPages}
        </div>
        <nav>
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage - 1)}>
                <i className="ri-arrow-left-s-line"></i>
              </button>
            </li>
            {getPageNumbers().map(page => (
              <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                <button className="page-link" onClick={() => onPageChange(page)}>
                  {page}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage + 1)}>
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </li>
          </ul>
        </nav>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">Lignes par page:</span>
          <select 
            className="form-select form-select-sm" 
            style={{ width: '70px' }}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    );
  };

  useEffect(() => {
    fetchCommandesCours();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchCommandesCours, 60000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchCommandesCours = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(
        `${API_BASE_URL}/commandes_cours.php`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/pages/signin2");
          throw new Error("Session expirée");
        }
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (type, id) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem("token");
      const action = type === 'fournisseur' ? 'detail_fournisseur' : 'detail_client';
      
      const response = await fetch(
        `${API_BASE_URL}/commandes_cours.php?action=${action}&id=${id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const result = await response.json();
      const commandeDetails = result.commande || result;
      setSelectedCommande(Array.isArray(commandeDetails) ? commandeDetails : [commandeDetails]);
      setDetailType(type);
      setShowDetailModal(true);
    } catch (err) {
      console.error("Erreur chargement détails:", err);
      alert("Erreur lors du chargement des détails");
    } finally {
      setLoadingDetails(false);
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'emise': { bg: '#f59e0b', text: 'À encaisser', icon: 'ri-time-line' },
      'payee_partielle': { bg: '#3b82f6', text: 'Partiellement payée', icon: 'ri-indeterminate-circle-line' },
      'envoyee': { bg: '#4361ee', text: 'Envoyée', icon: 'ri-send-plane-line' },
      'recue_partielle': { bg: '#fd7e14', text: 'Réception partielle', icon: 'ri-inbox-line' },
      'complete': { bg: '#10b981', text: 'Complète', icon: 'ri-checkbox-circle-line' },
      'partielle': { bg: '#fd7e14', text: 'Partielle', icon: 'ri-indeterminate-circle-line' },
      'en_attente': { bg: '#6b7280', text: 'En attente', icon: 'ri-time-line' }
    };
    const badge = badges[statut] || { bg: '#6b7280', text: statut, icon: 'ri-question-line' };
    return (
      <span className="status-badge" style={{ background: `${badge.bg}15`, color: badge.bg }}>
        <i className={`${badge.icon} me-1 fs-10`}></i>
        {badge.text}
      </span>
    );
  };

  const getPrioriteBadge = (priorite) => {
    const badges = {
      'expiree': { bg: '#ef4444', text: 'Expirée', icon: 'ri-alert-line', bgSoft: '#fef2f2' },
      'urgente': { bg: '#f59e0b', text: 'Urgente', icon: 'ri-alarm-warning-line', bgSoft: '#fffbeb' },
      'normale': { bg: '#10b981', text: 'Normale', icon: 'ri-check-line', bgSoft: '#ecfdf5' }
    };
    const badge = badges[priorite] || badges.normale;
    return (
      <span className="status-badge" style={{ background: badge.bgSoft, color: badge.bg }}>
        <i className={`${badge.icon} me-1 fs-10`}></i>
        {badge.text}
      </span>
    );
  };

  if (loading && !data) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="text-center">
              <div className="loading-animation mb-4">
                <div className="loading-ring">
                  <div className="loading-ring-inner"></div>
                </div>
                <i className="ri-shopping-cart-line loading-icon"></i>
              </div>
              <h5 className="fw-bold text-primary mb-2">Chargement des commandes</h5>
              <p className="text-muted small mb-0">Récupération des données en cours...</p>
              <div className="mx-auto mt-4" style={{ width: '200px' }}>
                <div className="progress" style={{ height: '3px', borderRadius: '3px', background: '#e9ecef' }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <style>{`
          .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
          .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #4361ee, #10b981); animation: pulse-ring 1.5s ease-in-out infinite; }
          .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
          .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #4361ee; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
          @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
          @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
        `}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <Alert variant="danger" className="border-0 shadow-sm rounded-3">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchCommandesCours}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  const paginatedClients = paginateData(data?.commandes_clients, currentPage, itemsPerPage);
  const totalClientsPages = getTotalPages(data?.commandes_clients?.length);

  const paginatedFournisseurs = paginateData(data?.commandes_fournisseurs, currentPage, itemsPerPage);
  const totalFournisseursPages = getTotalPages(data?.commandes_fournisseurs?.length);

  const paginatedEcheances = paginateData(data?.echeances, currentPage, itemsPerPage);
  const totalEcheancesPages = getTotalPages(data?.echeances?.length);

  const paginatedReceptions = paginateData(data?.receptions, currentPage, itemsPerPage);
  const totalReceptionsPages = getTotalPages(data?.receptions?.length);

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        
        {/* En-tête moderne */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                <i className="ri-shopping-cart-line fs-20 text-primary"></i>
              </div>
              <div>
                <h4 className="main-title mb-0 fw-bold">Suivi des commandes</h4>
                <p className="text-muted small mb-0">Gestion centralisée des commandes clients et fournisseurs</p>
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="form-check form-switch d-flex align-items-center">
              <input
                className="form-check-input me-2"
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                id="autoRefresh"
              />
              <label className="form-check-label small text-muted" htmlFor="autoRefresh">
                Auto-refresh
              </label>
            </div>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={fetchCommandesCours}
              className="rounded-pill px-3 d-flex align-items-center gap-1"
            >
              <i className="ri-refresh-line"></i>
              Actualiser
            </Button>
          </div>
        </div>

        {/* KPI Cards modernes */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-primary">
              <div className="kpi-icon">
                <i className="ri-truck-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Commandes fournisseurs</span>
                <h3 className="kpi-value">{data?.statistiques?.nb_commandes_fournisseurs || 0}</h3>
                <span className="kpi-unit">{formatCurrency(data?.statistiques?.valeur_commandes_fournisseurs || 0)}</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-success">
              <div className="kpi-icon">
                <i className="ri-file-list-3-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Commandes clients</span>
                <h3 className="kpi-value">{data?.statistiques?.nb_commandes_clients || 0}</h3>
                <span className="kpi-unit">{formatCurrency(data?.statistiques?.valeur_commandes_clients || 0)}</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-danger">
              <div className="kpi-icon">
                <i className="ri-error-warning-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Factures expirées</span>
                <h3 className="kpi-value">{data?.statistiques?.nb_factures_expirees || 0}</h3>
                <span className="kpi-unit">{formatCurrency(data?.statistiques?.montant_factures_expirees || 0)}</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-warning">
              <div className="kpi-icon">
                <i className="ri-inbox-archive-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Réceptions en attente</span>
                <h3 className="kpi-value">{data?.receptions?.length || 0}</h3>
                <span className="kpi-unit">commandes partielles</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Onglets principaux */}
        <Card className="border-0 shadow-sm rounded-3">
          <Card.Header className="bg-transparent border-0 pt-3 px-4">
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="custom-tabs">
              <Tab 
                eventKey="clients" 
                title={
                  <span className="d-flex align-items-center gap-2">
                    <i className="ri-user-line"></i>
                    <span>Commandes clients</span>
                    <Badge bg="primary" className="rounded-pill">{data?.commandes_clients?.length || 0}</Badge>
                  </span>
                }
              >
                <div className="table-responsive mt-3">
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">N° Facture</th>
                        <th>Date</th>
                        <th>Client</th>
                        <th>Échéance</th>
                        <th className="text-end">Montant</th>
                        <th className="text-end">Réglé</th>
                        <th className="text-end">Reste</th>
                        <th>Priorité</th>
                        <th>Statut</th>
                        <th className="text-center pe-4">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedClients?.map((commande) => {
                        const reste = commande.montant_ttc - commande.montant_regle;
                        const isExpired = commande.priorite === 'expiree';
                        
                        return (
                          <tr key={commande.id_facture} className={isExpired ? 'table-danger' : ''}>
                            <td className="ps-4 fw-semibold">
                              <Link to={`/ventes/facturesDetails/${commande.id_facture}`} className="table-link">
                                {commande.numero_facture}
                              </Link>
                            </td>
                            <td className="text-muted">{formatDate(commande.date_facture)}</td>
                            <td>
                              <div className="fw-semibold">{commande.client}</div>
                              <small className="text-muted">{commande.code_client}</small>
                            </td>
                            <td>
                              <div className="fw-semibold">{formatDate(commande.date_echeance)}</div>
                              <small className={commande.jours_restants < 0 ? 'text-danger' : 'text-muted'}>
                                {commande.jours_restants < 0 
                                  ? `Retard de ${Math.abs(commande.jours_restants)}j`
                                  : `Dans ${commande.jours_restants}j`
                                }
                              </small>
                            </td>
                            <td className="text-end">{formatCurrency(commande.montant_ttc)}</td>
                            <td className="text-end text-success">{formatCurrency(commande.montant_regle)}</td>
                            <td className="text-end text-danger fw-bold">{formatCurrency(reste)}</td>
                            <td>{getPrioriteBadge(commande.priorite)}</td>
                            <td>{getStatutBadge(commande.statut)}</td>
                            <td className="text-center pe-4">
                              <button 
                                className="action-icon"
                                onClick={() => handleViewDetails('client', commande.id_facture)}
                                title="Voir les détails"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <PaginationComponent 
                  currentPage={currentPage}
                  totalPages={totalClientsPages}
                  onPageChange={handlePageChange}
                />
              </Tab>

              <Tab 
                eventKey="fournisseurs" 
                title={
                  <span className="d-flex align-items-center gap-2">
                    <i className="ri-truck-line"></i>
                    <span>Commandes fournisseurs</span>
                    <Badge bg="success" className="rounded-pill">{data?.commandes_fournisseurs?.length || 0}</Badge>
                  </span>
                }
              >
                <div className="table-responsive mt-3">
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">N° Commande</th>
                        <th>Date</th>
                        <th>Fournisseur</th>
                        <th>Livraison prévue</th>
                        <th className="text-end">Montant</th>
                        <th>Réception</th>
                        <th>Statut</th>
                        <th className="text-center pe-4">Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedFournisseurs?.map((commande) => {
                        const progress = (commande.quantite_recue / commande.quantite_totale) * 100;
                        const isLate = commande.date_livraison_prevue < new Date().toISOString().split('T')[0];
                        
                        return (
                          <tr key={commande.id_commande_fournisseur}>
                            <td className="ps-4 fw-semibold">
                              <Link to={`/achats/commandes/${commande.id_commande_fournisseur}`} className="table-link">
                                {commande.numero_commande}
                              </Link>
                            </td>
                            <td className="text-muted">{formatDate(commande.date_commande)}</td>
                            <td>
                              <div className="fw-semibold">{commande.fournisseur}</div>
                              <small className="text-muted">{commande.code_fournisseur}</small>
                            </td>
                            <td>
                              <div className="fw-semibold">{formatDate(commande.date_livraison_prevue)}</div>
                              {isLate && <small className="text-danger">En retard</small>}
                            </td>
                            <td className="text-end">{formatCurrency(commande.montant_ttc)}</td>
                            <td style={{ minWidth: '140px' }}>
                              <div className="d-flex align-items-center gap-2">
                                <div className="progress-bar-custom" style={{ width: '80px', height: '5px' }}>
                                  <div className="progress-fill-custom" style={{ width: `${progress}%`, background: '#10b981' }}></div>
                                </div>
                                <small className="text-muted">
                                  {commande.quantite_recue}/{commande.quantite_totale}
                                </small>
                              </div>
                              <div className="mt-1">
                                {getStatutBadge(commande.etat_reception)}
                              </div>
                            </td>
                            <td>{getStatutBadge(commande.statut)}</td>
                            <td className="text-center pe-4">
                              <button 
                                className="action-icon"
                                onClick={() => handleViewDetails('fournisseur', commande.id_commande_fournisseur)}
                                title="Voir les détails"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <PaginationComponent 
                  currentPage={currentPage}
                  totalPages={totalFournisseursPages}
                  onPageChange={handlePageChange}
                />
              </Tab>

              <Tab 
                eventKey="echeances" 
                title={
                  <span className="d-flex align-items-center gap-2">
                    <i className="ri-calendar-check-line"></i>
                    <span>Échéances</span>
                    <Badge bg="warning" className="rounded-pill">{data?.echeances?.length || 0}</Badge>
                  </span>
                }
              >
                <div className="table-responsive mt-3">
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Facture</th>
                        <th>Client</th>
                        <th>Date facture</th>
                        <th>Date échéance</th>
                        <th className="text-end">Montant dû</th>
                        <th>Jours restants</th>
                        <th className="text-center pe-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEcheances?.map((echeance) => (
                        <tr key={echeance.id_facture}>
                          <td className="ps-4 fw-semibold table-link">{echeance.numero_facture}</td>
                          <td className="fw-semibold">{echeance.client}</td>
                          <td className="text-muted">{formatDate(echeance.date_facture)}</td>
                          <td className={echeance.jours_restants < 0 ? 'text-danger fw-bold' : ''}>
                            {formatDate(echeance.date_echeance)}
                          </td>
                          <td className="text-end fw-bold text-primary">{formatCurrency(echeance.montant_du)}</td>
                          <td>
                            <span className={`status-badge ${echeance.jours_restants < 0 ? 'bg-danger-soft text-danger' : echeance.jours_restants <= 3 ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success'}`}>
                              {echeance.jours_restants < 0 
                                ? `Retard ${Math.abs(echeance.jours_restants)}j`
                                : `J-${echeance.jours_restants}`
                              }
                            </span>
                          </td>
                          <td className="text-center pe-4">
                            <button 
                              className="action-icon action-icon-primary"
                              onClick={() => navigate(`/ventes/facturesDetails/${echeance.id_facture}`)}
                              title="Encaisser"
                            >
                              <i className="ri-bank-card-line"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <PaginationComponent 
                  currentPage={currentPage}
                  totalPages={totalEcheancesPages}
                  onPageChange={handlePageChange}
                />
              </Tab>

              <Tab 
                eventKey="receptions" 
                title={
                  <span className="d-flex align-items-center gap-2">
                    <i className="ri-inbox-archive-line"></i>
                    <span>Réceptions attendues</span>
                    <Badge bg="info" className="rounded-pill">{data?.receptions?.length || 0}</Badge>
                  </span>
                }
              >
                <div className="table-responsive mt-3">
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Commande</th>
                        <th>Fournisseur</th>
                        <th>Livraison prévue</th>
                        <th className="text-end">Produits</th>
                        <th className="text-end">Qté restante</th>
                        <th className="text-center pe-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReceptions?.map((reception) => {
                        const isLate = reception.date_livraison_prevue < new Date().toISOString().split('T')[0];
                        return (
                          <tr key={reception.id_commande_fournisseur}>
                            <td className="ps-4 fw-semibold table-link">{reception.numero_commande}</td>
                            <td className="fw-semibold">{reception.fournisseur}</td>
                            <td className={isLate ? 'text-danger fw-bold' : 'text-muted'}>
                              {formatDate(reception.date_livraison_prevue)}
                              {isLate && <i className="ri-alert-line ms-1 fs-10"></i>}
                            </td>
                            <td className="text-end">{reception.nb_produits}</td>
                            <td className="text-end fw-bold text-warning">{formatNumber(reception.quantite_restante)}</td>
                            <td className="text-center pe-4">
                              <button 
                                className="action-icon action-icon-success"
                                onClick={() => navigate(`/achats/commandes/${reception.id_commande_fournisseur}`)}
                                title="Réceptionner"
                              >
                                <i className="ri-check-line"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <PaginationComponent 
                  currentPage={currentPage}
                  totalPages={totalReceptionsPages}
                  onPageChange={handlePageChange}
                />
              </Tab>
            </Tabs>
          </Card.Header>
        </Card>

        <Footer />
      </div>

      {/* Modal de détail */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered className="detail-modal">
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <div className="d-flex align-items-center gap-2">
            <div className={`p-2 rounded-3 bg-${detailType === 'fournisseur' ? 'success' : 'primary'} bg-opacity-10`}>
              <i className={`ri-${detailType === 'fournisseur' ? 'truck' : 'user'}-line fs-20 text-${detailType === 'fournisseur' ? 'success' : 'primary'}`}></i>
            </div>
            <Modal.Title className="fs-5 fw-bold">
              {detailType === 'fournisseur' ? 'Détail commande fournisseur' : 'Détail commande client'}
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          {loadingDetails ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="text-muted mb-0">Chargement des détails...</p>
            </div>
          ) : selectedCommande && selectedCommande.length > 0 ? (
            <>
              <div className="summary-card p-3 mb-4 rounded-3">
                <Row className="align-items-center">
                  <Col md={6}>
                    <div className="d-flex align-items-center gap-3">
                      <div className="summary-icon">
                        <i className="ri-shopping-bag-line fs-24 text-primary"></i>
                      </div>
                      <div>
                        <span className="text-muted small text-uppercase">Total articles</span>
                        <h5 className="mb-0 fw-bold">{selectedCommande.length}</h5>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-center gap-3">
                      <div className="summary-icon">
                        <i className="ri-stack-line fs-24 text-success"></i>
                      </div>
                      <div>
                        <span className="text-muted small text-uppercase">Quantité totale</span>
                        <h5 className="mb-0 fw-bold">
                          {selectedCommande.reduce((acc, item) => acc + (item.quantite || item.quantite_commandee || 0), 0)}
                        </h5>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="table-responsive">
                <Table className="align-middle mb-0 detail-table">
                  <thead>
                    <tr>
                      <th>Référence</th>
                      <th>Produit</th>
                      <th className="text-center">Quantité</th>
                      <th className="text-end">Prix unitaire</th>
                      <th className="text-end">Total HT</th>
                      {detailType === 'fournisseur' && <th className="text-center">Reçu</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCommande.map((ligne, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className="reference-badge">{ligne.reference}</span>
                        </td>
                        <td className="fw-semibold">{ligne.designation}</td>
                        <td className="text-center">{ligne.quantite || ligne.quantite_commandee}</td>
                        <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                        <td className="text-end fw-bold text-primary">{formatCurrency(ligne.montant_ligne_ht)}</td>
                        {detailType === 'fournisseur' && (
                          <td className="text-center">
                            <span className={`reception-badge ${(ligne.quantite_recue || 0) >= (ligne.quantite_commandee || ligne.quantite) ? 'completed' : 'pending'}`}>
                              <i className={`ri-${(ligne.quantite_recue || 0) >= (ligne.quantite_commandee || ligne.quantite) ? 'checkbox-circle' : 'time'}-line me-1`}></i>
                              {ligne.quantite_recue || 0}/{ligne.quantite_commandee || ligne.quantite}
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
              <p className="text-muted mb-0">Aucun détail disponible</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-4">
          <Button variant="light" onClick={() => setShowDetailModal(false)} className="rounded-pill px-4">
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        /* KPI Cards */
        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: #4361ee; }
        .kpi-success { border-left-color: #10b981; }
        .kpi-danger { border-left-color: #ef4444; }
        .kpi-warning { border-left-color: #f59e0b; }
        
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; }
        .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
        .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
        .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
        
        .kpi-content {
          flex: 1;
        }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.4rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .kpi-unit {
          font-size: 0.7rem;
          color: #6c757d;
        }
        
        /* Status Badge */
        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        /* Action Icons */
        .action-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          color: #6c757d;
          transition: all 0.2s;
          cursor: pointer;
        }
        .action-icon:hover {
          background: #4361ee;
          border-color: #4361ee;
          color: white;
        }
        .action-icon-success:hover {
          background: #198754;
          border-color: #198754;
          color: white;
        }
        .action-icon-primary:hover {
          background: #4361ee;
          border-color: #4361ee;
          color: white;
        }
        
        /* Table Link */
        .table-link {
          color: #4361ee;
          text-decoration: none;
          font-weight: 600;
        }
        .table-link:hover {
          text-decoration: underline;
        }
        
        /* Progress Bar */
        .progress-bar-custom {
          background: #e9ecef;
          border-radius: 10px;
          overflow: hidden;
        }
        .progress-fill-custom {
          height: 100%;
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        
        /* Reference Badge */
        .reference-badge {
          background: #f8f9fa;
          padding: 0.2rem 0.6rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-family: monospace;
        }
        
        /* Summary Card */
        .summary-card {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 12px;
        }
        .summary-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        /* Reception Badge */
        .reception-badge {
          display: inline-flex;
          align-items: center;
          padding: 0.2rem 0.6rem;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 500;
        }
        .reception-badge.completed {
          background: #ecfdf5;
          color: #10b981;
        }
        .reception-badge.pending {
          background: #fffbeb;
          color: #f59e0b;
        }
        
        /* Custom Tabs */
        .custom-tabs .nav-link {
          border: none;
          color: #6c757d;
          padding: 0.75rem 1.25rem;
          font-weight: 500;
          transition: all 0.2s;
        }
        .custom-tabs .nav-link:hover {
          color: #4361ee;
          background: #f8f9fa;
        }
        .custom-tabs .nav-link.active {
          color: #4361ee;
          border-bottom: 2px solid #4361ee;
          background: transparent;
        }
        
        /* Pagination Styles */
        .pagination {
          gap: 4px;
        }
        .pagination .page-item .page-link {
          border-radius: 8px;
          border: 1px solid #e9ecef;
          color: #495057;
          background: white;
          padding: 6px 12px;
          font-size: 0.75rem;
          transition: all 0.2s;
        }
        .pagination .page-item.active .page-link {
          background: #4361ee;
          border-color: #4361ee;
          color: white;
        }
        .pagination .page-item .page-link:hover:not(.disabled) {
          background: #e8f0fe;
          border-color: #4361ee;
          color: #4361ee;
        }
        .pagination .page-item.disabled .page-link {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .fs-10 { font-size: 0.625rem; }
        .rounded-3 { border-radius: 12px !important; }
      `}</style>
    </>
  );
}